import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { getTemplates } from '../../services/communicationsTemplateApi';
import { getNewsletters, createNewsletter, getTeacherMe } from '../../services/teacher';
import GhlEmailBuilderFrame, { isGhlEmailBuilderConfigured } from '../communications/GhlEmailBuilderFrame';

export default function NewsletterPage() {
  const showDesignTab = isGhlEmailBuilderConfigured();
  const [mainTab, setMainTab] = useState(0);

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [sent, setSent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [classroomLabel, setClassroomLabel] = useState('your class');
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tList, nRes, meRes] = await Promise.all([
        getTemplates().catch(() => []),
        getNewsletters(),
        getTeacherMe().catch(() => ({ data: {} })),
      ]);
      setTemplates(Array.isArray(tList) ? tList : []);
      const me = meRes?.data || meRes;
      const c = me?.assigned_classroom;
      if (c) {
        setClassroomLabel(`${c.label || c.class_num || 'Class'}${c.grade ? ` — ${c.grade}` : ''}`);
      }
      setSent(nRes.data?.results || nRes.data || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => String(x.id) === String(id));
    if (t) {
      if (t.subject) setSubject(t.subject);
      if (t.body_plain) setBody(t.body_plain);
      if (t.body_html) setBodyHtml(t.body_html);
    }
  };

  const canSendQuick = subject.trim() && body.trim();
  const canSendDesign = subject.trim() && (bodyHtml.trim() || body.trim());

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      await createNewsletter({
        subject: subject.trim(),
        body_plain: mainTab === 1 && bodyHtml.trim() ? body.trim() || bodyHtml.replace(/<[^>]+>/g, ' ').slice(0, 8000) : body.trim(),
        body_html: mainTab === 1 ? bodyHtml.trim() : '',
        template_id: templateId || undefined,
      });
      setSuccess('Newsletter sent!');
      setSubject('');
      setBody('');
      setBodyHtml('');
      setTemplateId('');
      setConfirmOpen(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
      <Typography variant="h6">Newsletters</Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {showDesignTab ? (
        <Tabs value={mainTab} onChange={(_, v) => setMainTab(v)}>
          <Tab label="Quick message" />
          <Tab label="Design email" />
        </Tabs>
      ) : null}

      {(mainTab === 0 || !showDesignTab) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="tpl-label">Template (optional)</InputLabel>
            <Select
              labelId="tpl-label"
              label="Template (optional)"
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id ?? t.slug} value={t.id ?? t.slug}>
                  {t.name || t.title || t.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} required fullWidth />
          <TextField
            label="Message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            fullWidth
            multiline
            minRows={6}
          />
          <Button
            variant="contained"
            size="large"
            disabled={!canSendQuick || sending}
            onClick={() => setConfirmOpen(true)}
          >
            Send to My Class
          </Button>
        </Box>
      )}

      {showDesignTab && mainTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Use the builder below, then paste exported HTML into the box (or type HTML) so we can store it with the
            send. Plain text is used for SMS/voice when needed.
          </Typography>
          <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} required fullWidth />
          <TextField
            label="HTML (paste from GHL or edit)"
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            fullWidth
            multiline
            minRows={6}
          />
          <TextField
            label="Plain text / SMS fallback (optional if HTML only)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          <GhlEmailBuilderFrame />
          <Button
            variant="contained"
            size="large"
            disabled={!canSendDesign || sending}
            onClick={() => setConfirmOpen(true)}
          >
            Send to My Class
          </Button>
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 2 }}>
        Sent newsletters
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {sent.length === 0 ? (
          <Typography color="text.secondary">No newsletters sent yet.</Typography>
        ) : (
          sent.map((n) => (
            <Box
              key={n.id}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                cursor: 'pointer',
              }}
              onClick={() => setExpandedId(expandedId === n.id ? null : n.id)}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {n.subject}
                </Typography>
                <Chip size="small" label={n.status || '—'} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
              </Typography>
              <Collapse in={expandedId === n.id}>
                {n.body_html ? (
                  <Box
                    component="iframe"
                    title="Newsletter HTML"
                    sandbox="allow-same-origin"
                    srcDoc={n.body_html}
                    sx={{ width: '100%', minHeight: 200, border: 0, mt: 1 }}
                  />
                ) : null}
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                  {n.body_plain}
                </Typography>
              </Collapse>
            </Box>
          ))
        )}
      </Box>

      <Dialog open={confirmOpen} onClose={() => !sending && setConfirmOpen(false)}>
        <DialogTitle>Send newsletter?</DialogTitle>
        <DialogContent>Send this newsletter to all parents in {classroomLabel}?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSend} disabled={sending}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
